import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, User as UserIcon, LogOut, ChevronRight, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyShippingAddress = {
  full_name: "",
  address: "",
  city: "",
  postal_code: "",
  country: "Italia",
  phone: "",
};

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("shipping");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shippingAddress, setShippingAddress] = useState(emptyShippingAddress);
  const [shippingLoading, setShippingLoading] = useState(true);
  const [shippingSaving, setShippingSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersResponse, shippingResponse] = await Promise.all([
          axios.get(`${API}/orders`, { withCredentials: true }),
          axios.get(`${API}/user/shipping-address`, { withCredentials: true }),
        ]);

        setOrders(ordersResponse.data);

        const saved = shippingResponse.data?.shipping_address;
        if (saved) {
          setShippingAddress({
            full_name: saved.full_name || "",
            address: saved.address || "",
            city: saved.city || "",
            postal_code: saved.postal_code || "",
            country: saved.country || "Italia",
            phone: saved.phone || "",
          });
        }
      } catch (err) {
        console.error("Error fetching profile data:", err);
      } finally {
        setLoading(false);
        setShippingLoading(false);
      }
    };

    fetchData();
  }, []);

  const saveShippingAddress = async (e) => {
    e.preventDefault();

    if (!shippingAddress.full_name || !shippingAddress.address || !shippingAddress.city || !shippingAddress.postal_code) {
      toast.error("Compila i campi obbligatori dell'indirizzo");
      return;
    }

    setShippingSaving(true);
    try {
      await axios.put(`${API}/user/shipping-address`, shippingAddress, { withCredentials: true });
      toast.success("Informazioni di spedizione salvate");
    } catch (err) {
      console.error("Error saving shipping address:", err);
      toast.error("Errore nel salvataggio dell'indirizzo");
    } finally {
      setShippingSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-gray-200";
      case "paid":
        return "bg-accent-blue";
      case "processing":
        return "bg-accent-yellow";
      case "shipped":
        return "bg-accent-pink text-white";
      case "delivered":
        return "bg-accent-green";
      default:
        return "bg-gray-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "In attesa";
      case "paid":
        return "Pagato";
      case "processing":
        return "In preparazione";
      case "shipped":
        return "Spedito";
      case "delivered":
        return "Consegnato";
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <section className="bg-black text-white py-12 border-b-4 border-accent-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-20 h-20 border-4 border-white" />
            ) : (
              <div className="w-20 h-20 border-4 border-white bg-accent-pink flex items-center justify-center">
                <UserIcon size={40} />
              </div>
            )}
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl">{user?.name}</h1>
              <p className="font-mono text-gray-400">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <nav className="border-2 border-black p-4 space-y-2 sticky top-32">
              <button
                onClick={() => setActiveTab("shipping")}
                className={`w-full flex items-center gap-3 p-3 border-2 border-black ${
                  activeTab === "shipping" ? "bg-black text-white" : "hover:bg-gray-50"
                }`}
              >
                <MapPin size={20} />
                <span className="font-body font-semibold">Spedizione</span>
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                className={`w-full flex items-center gap-3 p-3 border-2 border-black ${
                  activeTab === "orders" ? "bg-black text-white" : "hover:bg-gray-50"
                }`}
              >
                <Package size={20} />
                <span className="font-body font-semibold">I Miei Ordini</span>
              </button>
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 p-3 text-red-600 hover:bg-red-50 border-2 border-transparent hover:border-red-600"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
                <span className="font-body font-semibold">Esci</span>
              </button>
            </nav>
          </div>

          <div className="lg:col-span-3">
            {activeTab === "shipping" && (
            <section>
              <h2 className="font-heading text-3xl mb-6">INFORMAZIONI DI SPEDIZIONE</h2>

              <div className="border-4 border-black p-6">
                {shippingLoading ? (
                  <div className="font-mono">Caricamento...</div>
                ) : (
                  <form onSubmit={saveShippingAddress} className="space-y-4">
                    <div>
                      <Label htmlFor="profile-full-name" className="font-mono text-xs">NOME COMPLETO *</Label>
                      <Input
                        id="profile-full-name"
                        value={shippingAddress.full_name}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, full_name: e.target.value })}
                        className="border-2 border-black rounded-none"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="profile-address" className="font-mono text-xs">INDIRIZZO *</Label>
                      <Input
                        id="profile-address"
                        value={shippingAddress.address}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                        className="border-2 border-black rounded-none"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="profile-city" className="font-mono text-xs">CITTA' *</Label>
                        <Input
                          id="profile-city"
                          value={shippingAddress.city}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                          className="border-2 border-black rounded-none"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="profile-postal" className="font-mono text-xs">CAP *</Label>
                        <Input
                          id="profile-postal"
                          value={shippingAddress.postal_code}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                          className="border-2 border-black rounded-none"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="profile-country" className="font-mono text-xs">PAESE</Label>
                        <Input
                          id="profile-country"
                          value={shippingAddress.country}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                          className="border-2 border-black rounded-none"
                        />
                      </div>
                      <div>
                        <Label htmlFor="profile-phone" className="font-mono text-xs">TELEFONO</Label>
                        <Input
                          id="profile-phone"
                          value={shippingAddress.phone}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                          className="border-2 border-black rounded-none"
                        />
                      </div>
                    </div>

                    <button className="btn-brutal" type="submit" disabled={shippingSaving}>
                      {shippingSaving ? "Salvataggio..." : "Salva informazioni spedizione"}
                    </button>
                  </form>
                )}
              </div>
            </section>
            )}

            {activeTab === "orders" && (
            <section>
              <h2 className="font-heading text-3xl mb-6">I MIEI ORDINI</h2>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="border-2 border-black p-6 animate-pulse">
                      <div className="h-6 bg-gray-200 w-1/3 mb-4"></div>
                      <div className="h-4 bg-gray-200 w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="border-2 border-black p-12 text-center">
                  <Package size={48} className="mx-auto mb-4 text-gray-400" />
                  <h3 className="font-heading text-2xl mb-2">NESSUN ORDINE</h3>
                  <p className="font-body text-gray-600 mb-6">Non hai ancora effettuato nessun ordine</p>
                  <Link to="/abbigliamento" className="btn-brutal inline-flex items-center">
                    INIZIA LO SHOPPING
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, index) => (
                    <motion.div
                      key={order.order_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/ordine/${order.order_id}`}
                        className="block border-2 border-black p-6 hover:shadow-brutal transition-all"
                        data-testid={`order-${order.order_id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-mono text-sm text-gray-500">#{order.order_id}</span>
                              <span className={`font-mono text-xs uppercase px-3 py-1 border-2 border-black ${getStatusColor(order.status)}`}>
                                {getStatusText(order.status)}
                              </span>
                            </div>
                            <p className="font-body">
                              {order.items.length} articol{order.items.length === 1 ? "o" : "i"} - EUR {order.total.toFixed(2)}
                            </p>
                            <p className="font-mono text-sm text-gray-500 mt-1">
                              {new Date(order.created_at).toLocaleDateString("it-IT", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 text-accent-pink">
                            <span className="font-body font-semibold">Dettagli</span>
                            <ChevronRight size={20} />
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4 overflow-x-auto">
                          {order.items.slice(0, 4).map((item, i) => (
                            <div key={i} className="w-16 h-16 flex-shrink-0 border-2 border-black overflow-hidden">
                              <img src={item.product?.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {order.items.length > 4 && (
                            <div className="w-16 h-16 flex-shrink-0 border-2 border-black bg-gray-100 flex items-center justify-center">
                              <span className="font-mono text-sm">+{order.items.length - 4}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
